import { useMemo, useState } from "react";
import { Card } from "../../components/atoms";
import { TgScreen } from "../../components/TgScreen";
import { Ico } from "../../lib/icons";
import { isValidPhrase, normalizePhrase } from "../crypto/mnemonic";
import { useWallet } from "../context";
import { haptic } from "../../lib/haptics";

export function ImportSheet({
  onBack,
  onDone,
}: {
  onBack?: () => void;
  onDone?: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { setup } = useWallet();

  const wordCount = useMemo(() => normalizePhrase(text).split(" ").filter(Boolean).length, [text]);
  const valid = wordCount === 12 || wordCount === 24
    ? isValidPhrase(text)
    : false;

  const submit = async () => {
    setErr(null);
    if (!valid) {
      haptic("error");
      setErr("That doesn't look like a valid BIP-39 mnemonic. Should be 12 or 24 words.");
      return;
    }
    haptic("medium");
    setBusy(true);
    try {
      await setup(text);
      haptic("success");
      onDone?.();
    } catch (e) {
      haptic("error");
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <TgScreen
      title="Import wallet"
      onBack={onBack}
      mainButton={
        busy
          ? { text: "Importing…", loading: true, disabled: true }
          : valid
            ? { text: "Import wallet", onClick: submit }
            : { text: text.length > 0 ? "Invalid mnemonic" : "Paste your phrase", disabled: true }
      }
    >
      <div style={{ padding: "12px 16px 24px" }}>
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--hint)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
            }}
          >
            Your 12-word or 24-word phrase
          </div>
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setErr(null);
            }}
            placeholder="word1 word2 word3 ..."
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            rows={4}
            style={{
              width: "100%",
              background: "var(--bg-3)",
              border: err ? "1px solid var(--danger)" : "1px solid var(--hairline)",
              borderRadius: 10,
              padding: "10px 12px",
              color: "var(--text)",
              fontFamily: "var(--font-mono)",
              fontSize: 14,
              outline: "none",
              resize: "vertical",
              minHeight: 96,
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              minHeight: 18,
            }}
          >
            <button
              onClick={async () => {
                haptic("light");
                try {
                  const t = await navigator.clipboard.readText();
                  if (t) setText(t.trim());
                } catch {
                  // ignore — clipboard permissions denied
                }
              }}
              style={{
                background: "var(--bg-2)",
                color: "var(--text-soft)",
                border: "none",
                borderRadius: 8,
                padding: "5px 10px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              Paste
            </button>
            <div style={{ fontSize: 12, color: valid ? "var(--success)" : "var(--hint)" }}>
              {wordCount} word{wordCount === 1 ? "" : "s"}
              {valid && " · valid"}
            </div>
          </div>
        </Card>

        {err && (
          <div
            className="shake"
            style={{
              padding: 12,
              borderRadius: 10,
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.4)",
              color: "var(--danger)",
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "6px 4px 0",
            fontSize: 12,
            color: "var(--hint)",
            lineHeight: 1.45,
          }}
        >
          <Ico.shieldLock style={{ color: "var(--success)", marginTop: 1, flexShrink: 0 }} />
          <span>
            Your phrase is encrypted on this device with a random key — we never see it, neither
            does Telegram.
          </span>
        </div>
      </div>
    </TgScreen>
  );
}
